import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Token-first form-field foundation (ADR-015 amendment 2026-07-07, DS-6/DS-7/NF-10).
 * `Field` is the shared label + description + error wrapper every text-like control
 * composes so labelling/`aria-describedby`/`aria-invalid` are wired consistently.
 * `fieldControlClassName` is the shared token control surface (border, ≥44px via
 * `min-h-11`, visible token `:focus-visible` ring). No raw hex/px.
 */
export const fieldControlClassName = cn(
  'block w-full min-h-11 rounded-md border border-border bg-surface px-3 py-2 text-base text-text-primary',
  'transition-colors ease-ds placeholder:text-text-secondary',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-60',
);

export interface FieldProps {
  /** id of the control this field labels (used for `htmlFor` + derived describedby ids). */
  htmlFor: string;
  label: ReactNode;
  description?: ReactNode | undefined;
  error?: ReactNode | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
  children: ReactNode;
}

export function Field({
  htmlFor,
  label,
  description,
  error,
  required,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-text-primary">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-danger">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {description ? (
        <p id={`${htmlFor}-description`} className="text-sm text-text-secondary">
          {description}
        </p>
      ) : null}
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Compose the `aria-describedby` value for a field control from the description/error
 * ids `Field` renders (plus any caller-supplied extra ids). Returns `undefined` when
 * nothing applies so the attribute is omitted rather than empty.
 */
export function fieldDescribedBy(
  id: string,
  opts: { description?: unknown; error?: unknown; extra?: string | undefined },
): string | undefined {
  const ids = [
    opts.description ? `${id}-description` : null,
    opts.error ? `${id}-error` : null,
    opts.extra ?? null,
  ].filter((value): value is string => Boolean(value));
  return ids.length > 0 ? ids.join(' ') : undefined;
}
